<script setup>
/**
 * Dining Profile → Notes — the member's general dining notes: the things they told RM
 * that belong to them, not to one restaurant ("I don't like loud restaurants",
 * "Prefer patio seating with a good water view").
 *
 * Founder review, 2026-09-17: these must NOT be mixed into My Favorites, which is a
 * list of restaurants. They live here, newest first, with the date they were saved.
 * Restaurant-specific notes stay under Favorites → Notes and on the restaurant page.
 * One durable source: ProfileEntry (RM-294), read through GET /profile/general-notes.
 */
import { onMounted, ref } from 'vue'
import axios from '@axios'

import { relativeAgo } from '@/utils/relativeAgo'

const notes = ref([])
const loading = ref(true)
// Owner-only: the service refuses a spouse / personal-admin simulation (403). The
// Notes tile is hidden for those actors, but /notes is a real URL — a refusal must
// read as a refusal, never as "you have no notes".
const forbidden = ref(false)
const saving = ref(false)
const composer = ref('')
const editingId = ref(null)

const load = async () => {
  loading.value = true
  try {
    const { data } = await axios.get('/profile/general-notes')

    notes.value = Array.isArray(data) ? data : []
    forbidden.value = false
  } catch (error) {
    notes.value = []
    forbidden.value = error?.response?.status === 403
  } finally {
    loading.value = false
  }
}

const save = async () => {
  const text = (composer.value || '').trim()
  if (!text) return
  saving.value = true
  try {
    if (editingId.value) {
      await axios.put(`/profile/entries/${editingId.value}`, { text })
    } else {
      // No restaurantId: a general entry, exactly what RM AI saves for "I don't like loud restaurants"
      await axios.post('/profile/entries', { text })
    }
    composer.value = ''
    editingId.value = null
    await load()
  } catch (error) {
    console.error('Error saving note:', error)
  } finally {
    saving.value = false
  }
}

const startEdit = note => {
  // eslint-disable-next-line no-alert
  if (composer.value.trim() && !window.confirm('Discard what you have typed and edit this note instead?')) return
  editingId.value = note.entryId
  composer.value = note.note
}

const cancelEdit = () => {
  editingId.value = null
  composer.value = ''
}

const remove = async note => {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Remove this note?')) return
  saving.value = true
  try {
    await axios.delete(`/profile/entries/${note.entryId}`)
    await load()
  } catch (error) {
    console.error('Error removing note:', error)
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<template>
  <section
    id="notes"
    class="dining-notes"
  >
    <h3 class="dining-notes__title">
      Notes
    </h3>
    <p class="dining-notes__hint">
      What you've told RM about how you like to dine. Private to your Profile.
    </p>

    <div
      v-if="loading"
      class="dining-notes__loading"
    >
      <VProgressCircular
        indeterminate
        color="primary"
        size="20"
        width="2"
      />
    </div>

    <p
      v-else-if="forbidden"
      class="dining-notes__empty"
    >
      These notes are private to the member who wrote them.
    </p>

    <ul
      v-else-if="notes.length"
      class="dining-notes__list"
    >
      <li
        v-for="note in notes"
        :key="note.entryId"
        class="dining-note"
      >
        <p class="dining-note__text">
          {{ note.note }}
        </p>
        <div class="dining-note__meta">
          <span>{{ relativeAgo(note.createdAt) }} ago</span>
          <button
            type="button"
            class="dining-note__action"
            @click="startEdit(note)"
          >
            Edit
          </button>
          <button
            type="button"
            class="dining-note__action"
            :disabled="saving"
            @click="remove(note)"
          >
            Remove
          </button>
        </div>
      </li>
    </ul>

    <p
      v-else
      class="dining-notes__empty"
    >
      Nothing here yet. Tell RM AI how you like to dine and it lands here.
    </p>

    <VTextarea
      v-if="!forbidden"
      v-model="composer"
      :placeholder="editingId ? 'Edit this note…' : 'Add a note — how you like to dine, what to avoid…'"
      rows="2"
      auto-grow
      variant="outlined"
      density="compact"
      hide-details="auto"
      class="dining-notes__composer"
    />
    <div
      v-if="!forbidden"
      class="dining-notes__actions"
    >
      <VBtn
        v-if="editingId"
        variant="text"
        size="small"
        @click="cancelEdit"
      >
        Cancel
      </VBtn>
      <VBtn
        color="primary"
        variant="flat"
        size="small"
        rounded="lg"
        :disabled="!composer.trim()"
        :loading="saving"
        @click="save"
      >
        {{ editingId ? 'Save changes' : 'Add note' }}
      </VBtn>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.dining-notes {
  padding: 8px 16px 16px;
}

.dining-notes__title {
  margin: 0;
  font-family: var(--ds-font-heading);
  font-size: 20px;
  font-weight: var(--ds-font-medium, 500);
  line-height: 1.2;
  color: var(--ds-brand-ink, inherit);
}

.dining-notes__hint {
  margin: 2px 0 8px;
  font-family: var(--ds-font-body);
  font-size: 13.5px;
  line-height: 1.3;
  color: var(--ds-text-body);
}

.dining-notes__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.dining-note {
  padding: 10px 0;
  border-bottom: 1px solid var(--ds-border-subtle);
}

.dining-note:last-child {
  border-bottom: none;
}

.dining-note__text {
  margin: 0;
  font-size: 15px;
  line-height: 1.4;
  white-space: pre-line;
  overflow-wrap: anywhere;
  color: var(--ds-text-primary, inherit);
}

.dining-note__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  font-size: 12.5px;
  color: var(--ds-text-body);
}

.dining-note__action {
  padding: 0;
  font-size: 12.5px;
  color: var(--ds-brand-primary-dark);
  background: none;
  border: none;
  cursor: pointer;
}

.dining-notes__empty,
.dining-notes__loading {
  margin: 4px 0 0;
  font-size: 13.5px;
  color: var(--ds-text-body);
}

.dining-notes__composer {
  margin-top: 12px;
}

.dining-notes__composer :deep(textarea) {
  font-size: 16px; /* iOS must not zoom the page on focus */
}

.dining-notes__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
</style>
